import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { CellClassRules, ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-enterprise';
import * as moment from 'moment';
import { MessageService } from 'primeng/api';
import { ReportService } from '../..';
import * as Papa from 'papaparse';
import 'ag-grid-enterprise';
import { ImportExportComponent } from '@app/_components/_helpers/import-export/import-export.component';

@Component({
    selector: 'app-res-master-escrow',
    templateUrl: './res-master-escrow.component.html',
    styleUrls: ['./res-master-escrow.component.css'],
    standalone: false
})
export class ResMasterEscrowComponent implements OnInit {

	@Input() showSearchUI = true;
	@ViewChild(ImportExportComponent) importExportComponent: ImportExportComponent;
	
	constructor(
		public messageService: MessageService,
		public reportService: ReportService) 
		{ 
			this.defaultColDef = {
				sortable: true,
				filter: true,
				resizable: true,
			};

			this.gridOptions = {
				animateRows: true,
				enableRangeSelection: true,
				pagination: true,
				paginationPageSize: 2500,
				alwaysShowHorizontalScroll: true,

				autoGroupColumnDef: {
					headerName: 'Reservation',
					field: 'reservationId',
					pinned: 'left',
					cellRenderer: 'agGroupCellRenderer',
					cellRendererParams: {
						suppressCount: false,
						footerValueGetter: (params: any) => {
							if (params.node.level === -1) {
							return 'Grand Total';   // for overall total
							}
							return `Subtotal (${params.value})`; // for group subtotal
						}
					}
				},
				groupIncludeFooter: true,
  				groupIncludeTotalFooter: true,
				suppressAggFuncInHeader: true,
				groupDisplayType : 'multipleColumns',
				tooltipShowDelay: 0,
				popupParent: document.body  
			};
		}


	// forms
	formLoading = false;
	rowData: any[] = [];
	columnDefs: ColDef[] = [];
	gridOptions: GridOptions;
	defaultColDef: ColDef;

	columnDescriptions: Record<string, string> = {
		calendarDate: "The specific calendar date of record.",
		calendarYear: "Year corresponding to the calendar date.",
		calendarMonth: "Month corresponding to the calendar date.",
		propertyCode: "Unique identifier for the property.",
		orderId: "Unique ID assigned to the booking order.",
		reservationId: "Unique ID assigned to the reservation.",
		checkInDate: "Guest’s scheduled check-in date.",
		checkOutDate: "Guest’s scheduled check-out date.",
		resStatus: "Status of the reservation (e.g., confirmed, canceled, pending).",
		saleDate: "Date when the booking was made.",
		pendingNights: "Nights booked but not yet completed.",
		spentNights: "Nights already stayed by the guest.",
		holdAmount: "Escrow amount currently held for the reservation (temporarily blocked funds, not yet available).",
		freeAmount: "Escrow amount that has been unblocked and is available for use (released funds).",
		paymentRcvdGrossPNT: "Gross payment received per night (before deductions).",
		creditIssuedPNT: "Credit or refund issued per night.",
		paymentRcvdNetPNT: "Daily portion of freeAmount applied to each night of the reservation (net payment per night from escrow).",
		revNightlyratePNT: "Revenue from nightly rate per night.",
		revNightlyrateTaxPNT: "Tax collected on nightly rate per night.",
		revInvoiceDiscountPNT: "Discounts applied per night on invoice.",
		revOtherFeePNT: "Revenue from other fees per night (e.g., cleaning, service).",
		revOtherFeeTaxPNT: "Tax collected on other fees per night.",
		totalHoldAmount: "Total escrow amount held across the reservation.",
		totalFreeAmount: "Total escrow amount unblocked and available across the reservation."
	};

	// Explicit list of numeric fields for aggregation
	numericFields: string[] = [
		"pendingNights",
		"spentNights",
		"holdAmount",
		"freeAmount",
		"paymentRcvdGrossPNT",
		"creditIssuedPNT",
		"paymentRcvdNetPNT",
		"revNightlyratePNT",
		"revNightlyrateTaxPNT",
		"revInvoiceDiscountPNT",
		"revOtherFeePNT",
		"revOtherFeeTaxPNT",
		"totalHoldAmount",
		"totalFreeAmount"
	];

	pinnedColumns: string[] = [
		'reservationId',
		'calendarDate',
		'propertyCode',
		'checkInDate',
		'checkOutDate',
		// 'pendingNights',
		// 'spentNights',
		// 'holdAmount',
		// 'freeAmount',
		// 'paymentRcvdNetPNT',
		// 'revNightlyratePNT',
		// 'revNightlyrateTaxPNT',
		// 'revInvoiceDiscountPNT',
		// 'revOtherFeePNT',
		// 'revOtherFeeTaxPNT'
	];

	ngOnInit(): void {

	}

	loadReport(searchForm) {
		try {
			this.formLoading = true;
			//Defualt to show By Day if report type is not selected or selected as By Reservation
			if(!searchForm.reportType || (searchForm.reportType && searchForm.reportType == 1))
			{
				searchForm.reportType = 2;
			}
			searchForm.downloadType = 1;
			searchForm.reportName = "escrowsReport";

			// transform reservationStatusList to only values
			if (searchForm.reservationStatusList && Array.isArray(searchForm.reservationStatusList)) {
				searchForm.reservationStatusList = searchForm.reservationStatusList.map(s =>
					s != null && typeof s === 'object' && 'value' in s ? s.value : s);
			}

			console.log('Request data', searchForm);
			searchForm.saleDateStart = this.addLocalTimeToDateTime(new Date(searchForm.saleDateStart));
			searchForm.saleDateEnd = this.addLocalTimeToDateTime(new Date(searchForm.saleDateEnd));
			searchForm.startDate = this.addLocalTimeToDateTime(new Date(searchForm.startDate));
			searchForm.endDate = this.addLocalTimeToDateTime(new Date(searchForm.endDate));
			this.reportService.resMasterReport(searchForm).subscribe(async (res: any) => {
			
				try
				{
					let csvText: string;
					if (res && typeof res === 'string' && res.startsWith('http')) {
						// Response is URL → fetch CSV
						const response = await fetch(res);
						if (!response.ok) {
							throw new Error(`Failed to fetch CSV from URL: ${response.statusText}`);
						}
						csvText = await response.text();
					} else {
						// Response is already CSV content
						csvText = res;
					}

					// Parse CSV into JSON
					const parsed = Papa.parse(csvText, { header: true, skipEmptyLines: true });

					this.rowData = parsed.data.map((row: any) => {
						return {
							...row,
							pendingNights: +row.pendingNights,
							spentNights: +row.spentNights,
							holdAmount: +row.holdAmount,
							freeAmount: +row.freeAmount,
							paymentRcvdGrossPNT: +row.paymentRcvdGrossPNT,
							creditIssuedPNT: +row.creditIssuedPNT,
							paymentRcvdNetPNT: +row.paymentRcvdNetPNT,
							revNightlyratePNT: +row.revNightlyratePNT,
							revNightlyrateTaxPNT: +row.revNightlyrateTaxPNT,
							revInvoiceDiscountPNT: +row.revInvoiceDiscountPNT,
							revOtherFeePNT: +row.revOtherFeePNT,
							revOtherFeeTaxPNT: +row.revOtherFeeTaxPNT,
							totalHoldAmount: +row.totalHoldAmount,
							totalFreeAmount: +row.totalFreeAmount,
							calendarMonth: +row.calendarMonth,
							calendarYear: +row.calendarYear
						};
					});

					// Dynamically build columns
					this.columnDefs = parsed.meta.fields?.map((field: string) => {
						if (field === 'reservationId') {
							return {
								headerName: 'Reservation Id',
								field: 'reservationId',
								rowGroup: true,
								hide: true,
								cellRenderer: (params) => {
									// for normal rows
									const orderId = params.data?.orderId;
									const reservationId = params.data?.reservationId;

									// for group rows (when rowGroup = true, params.data is undefined)
									if (params.node.group) {
										const groupOrderId = params.node.allLeafChildren?.[0]?.data?.orderId;
										const groupReservationId = params.value;

										if (groupOrderId && groupReservationId) {
										return `
											<a href="https://admin.kbmhawaii.com/order/${groupOrderId}"
											target="_blank"
											rel="noopener noreferrer"
											style="color: blue; text-decoration: underline; cursor: pointer;">
											${groupReservationId}
											</a>
										`;
										}
										return groupReservationId || '';
									}

									// normal row rendering
									if (orderId && reservationId) {
										return `
										<a href="https://admin.kbmhawaii.com/order/${orderId}"
											target="_blank"
											rel="noopener noreferrer"
											style="color: blue; text-decoration: underline; cursor: pointer;">
											${reservationId}
										</a>
										`;
									}

									return reservationId || '';
								}
							};
						}
						const isNumeric = this.numericFields.includes(field);
						const def: any = {
							headerName: this.prettyHeader(field),
							field,
							headerTooltip: this.columnDescriptions[field] || '',
							aggFunc: isNumeric ? 'sum' : undefined,
							enableValue: isNumeric,
							allowedAggFuncs: ['sum'],
							valueGetter: isNumeric
								? (params) => (params.node.group ? null : Number(params.data?.[field] || 0))
								: undefined,
							valueFormatter: isNumeric
								? (params) => this.formatNumber(params.value)
								: undefined
						};
						if (this.pinnedColumns.includes(field)) {
							def.pinned = 'left';
						}
						return def;
					}) || [];

					// Reorder so pinned columns appear first (in given order)
					// this.columnDefs.sort((a, b) => {
					// 	const ai = this.pinnedColumns.indexOf(a.field);
					// 	const bi = this.pinnedColumns.indexOf(b.field);
					// 	if (ai === -1 && bi === -1) return 0; // neither pinned
					// 	if (ai === -1) return 1;              // a not pinned, b pinned → b first
					// 	if (bi === -1) return -1;             // b not pinned, a pinned → a first
					// 	return ai - bi;                       // both pinned → keep order from array
					// });

					// Auto-size columns after grid has rendered new data
					setTimeout(() => {
					if (this.gridOptions.api && this.gridOptions.columnApi) {
						const allColumnIds: string[] = [];
						(this.gridOptions as any).columnApi.getAllColumns().forEach((col) => {
						allColumnIds.push(col.getId());
						});
						(this.gridOptions as any).columnApi.autoSizeColumns(allColumnIds, false);
					}
					}, 0);
					this.formLoading = false;
				} catch (error) {
					console.error('CSV fetch/parse error:', error);
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: 'Failed to fetch/parse report',
					});
				} finally {
					// Always stop loader no matter success/fail
					this.formLoading = false;
				}
			},
			(err) => {
				console.error('API call failed:', err);
				this.formLoading = false;
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Report API call failed',
				});
			}
		);
	} catch (error) {
					console.error('CSV fetch/parse error:', error);
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: 'Failed to fetch/parse report',
					});
				}
	}

	exportSelected(exporttype: any) {
   		 this.importExportComponent.exporData(exporttype, this.rowData, 'Res Master');
	}

	// Format header names
	prettyHeader(field: string): string {
		return field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
	}

	formatNumber(value: any): string {
		return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(value);
	}

	onGridReady(params: any) {
		(this.gridOptions as any).api = params.api;
  		(this.gridOptions as any).columnApi = params.columnApi;
	}

	/**
	 * Add time plus local time GMT hours as http request change time to UTC time
	 * @param datetime 
	 * @returns 
	 */
	addLocalTimeToDateTime(datetime) {
		return new Date(datetime.getTime() - (datetime.getTimezoneOffset() * 60000));
	}

	ngAfterViewInit(): void {
	}

}
